import { Injectable } from '@angular/core';
import {
  referralReportsPageLoad,
  referralReportsPageLoadSuccess,
  referralReportsPageLoadFail,
  loadJobFunctions,
  loadJobFunctionsSuccess,
  loadJobFunctionsFail,
  loadJobDepartments,
  loadJobDepartmentsSuccess,
  loadJobDepartmentsFail,
  loadReferralsByJobDepartment,
  loadReferralsByJobDepartmentSuccess,
  loadReferralsByJobDepartmentFail,
  loadReferralsByReferralDepartment,
  loadReferralsByReferralDepartmentSuccess,
  loadReferralsByReferralDepartmentFail,
  loadReferralsByJobFunction,
  loadReferralsByJobFunctionSuccess,
  loadReferralsByJobFunctionFail,
  loadReferralsByReferralFunction,
  loadReferralsByReferralFunctionSuccess,
  loadReferralsByReferralFunctionFail,
  loadAllJobsByParams,
  loadAllJobsByParamsSuccess,
  loadAllJobsByParamsError,
  loadAggregatedTotalDirectPerPhasesData,
  loadAggregatedTotalDirectPerPhasesDataSuccess,
  loadAvgResponseTime,
  loadAvgResponseTimeSuccess,
  loadAvgResponseTimeFail,
  loadDashboardReportsSubmissions,
  loadDashboardReportsSuccessSubmissions,
  loadDashboardReportsFailSubmissions,
  loadDashboardReportsSuccessOffer,
  loadDashboardReportsFailOffer,
  loadDashboardReportsFail,
  loadDashboardReportsSuccess,
  loadDashboardReports,
  loadDashboardReportsOffer,
  loadOldestReferralDate,
  loadOldestReferralDateSuccess,
  loadOldestReferralDateFail,
  loadReferralCountByStage,
  loadReferralCountByStageSuccess,
  loadReferralCountByStageFail,
  loadAveragesPerJob,
  loadAveragesPerJobSuccess,
  loadAveragesPerJobFail,
  sendEmailReferralReport,
  sendEmailSuccessReport,
  sendEmailFailReport,
} from '@modules/reports/store/referral-reports.actions';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AvgResponseTime } from '@shared/models/avg-response-time';
import { HiringManagerService } from '@shared/services/hiring-manager.service';
import { ReferralReportsService } from '@shared/services/referral-reports.service';
import { of } from 'rxjs';
import { map, switchMap, catchError, pluck, filter } from 'rxjs/operators';
import { Job, JobService } from '@shared';
import { Pagination } from '@shared/pagination';
import { LoadAggregatedJobsData } from '@collections/aggregations';
import { Subpages } from '@modules/reports/models/subpages';
import { MetricsReportService } from '@shared/services/metrics-report.service';
import { DashboardReportModel } from '@shared/models/dashboard-reports';
import { ChartElement } from '../models/chart-element';
import { ReferralCountByStage } from '../models/referral-count-by-stage';
import { ReportDashboardService } from '@shared/services/report-dashboard.service';
import { ReportPipelineService } from '@shared/services/report-pipeline.service';

@Injectable()
export class ReferralReportsEffects {
  loadAllJobs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllJobsByParams),
      switchMap(({ params }) =>
        this.jobService.getByRecruiter({ params }).pipe(
          switchMap((response: Pagination<Job>) => [
            loadAggregatedTotalDirectPerPhasesData({
              jobsIds: response.content.map(({ id }) => id),
            }),
            LoadAggregatedJobsData({ jobsIds: response.content.map(({ id }) => id) }),
            loadAllJobsByParamsSuccess({ response }),
          ]),
          catchError((error) => of(loadAllJobsByParamsError({ error }))),
        ),
      ),
    ),
  );

  loadAggregatedTotalDirectPerPhasesData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAggregatedTotalDirectPerPhasesData),
      switchMap(({ jobsIds }) =>
        this.jobService.getDirectPerPhaseForGivenIds(jobsIds).pipe(
          map((allActiveJobsTotalDirectPerPhaseList) =>
            loadAggregatedTotalDirectPerPhasesDataSuccess({
              allActiveJobsTotalDirectPerPhaseList,
            }),
          ),
        ),
      ),
    ),
  );
  get$ = createEffect(() =>
    this.actions$.pipe(
      ofType(referralReportsPageLoad),
      pluck('filters'),
      switchMap(({ size = 10, page, ...filters }) =>
        this.service.post({ body: filters, params: { page, size } }).pipe(
          map((items) =>
            referralReportsPageLoadSuccess({
              payload: items,
            }),
          ),
          catchError((error) => of(referralReportsPageLoadFail({ error }))),
        ),
      ),
    ),
  );

  loadJobFunctions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadJobFunctions),
      switchMap(({ filters, functionsPage }) => {
        const params = {
          jobDepartment: filters.jobDepartment,
          dateStart: filters.dateStart,
          dateEnd: filters.dateEnd,
          jobStatus: filters.jobStatus,
        };
        switch (functionsPage) {
          case Subpages.REFERRAL_REPORT:
            return this.service.post<string[]>({ url: 'job-functions', body: filters }).pipe(
              map((items) => loadJobFunctionsSuccess({ jobFunctions: items })),
              catchError((error) => of(loadJobFunctionsFail({ error }))),
            );
          case Subpages.PIPELINE:
            return this.reportPipelineService.getJobFunctionsForPipeline(params).pipe(
              map((items) => loadJobFunctionsSuccess({ jobFunctions: items })),
              catchError((error) => of(loadJobFunctionsFail({ error }))),
            );
        }
      }),
    ),
  );

  loadJobDepartments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadJobDepartments),
      switchMap(({ filters, departmentsPage }) => {
        const params = {
          jobFunction: filters.jobFunction,
          jobStatus: filters.jobStatus,
          dateStart: filters.dateStart,
          dateEnd: filters.dateEnd,
        };

        switch (departmentsPage) {
          case Subpages.REFERRAL_REPORT:
            return this.service.post<string[]>({ url: 'job-departments', body: filters }).pipe(
              map((items) => loadJobDepartmentsSuccess({ jobDepartments: items })),
              catchError((error) => of(loadJobDepartmentsFail({ error }))),
            );
          case Subpages.PIPELINE:
            return this.reportPipelineService.getJobDepartmentsForPipeline(params).pipe(
              map((items) => loadJobDepartmentsSuccess({ jobDepartments: items })),
              catchError((error) => of(loadJobDepartmentsFail({ error }))),
            );
        }
      }),
    ),
  );

  loadReferralsByJobDepartment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadReferralsByJobDepartment),
      pluck('filters'),
      switchMap((filters) =>
        this.service.post<ChartElement[]>({ url: 'count-by-job-departments', body: filters }).pipe(
          map((elements) => loadReferralsByJobDepartmentSuccess({ elements })),
          catchError((error) => of(loadReferralsByJobDepartmentFail({ error }))),
        ),
      ),
    ),
  );

  loadReferralsByReferralDepartment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadReferralsByReferralDepartment),
      pluck('filters'),
      switchMap((filters) =>
        this.service
          .post<ChartElement[]>({ url: 'count-by-referral-departments', body: filters })
          .pipe(
            map((elements) => loadReferralsByReferralDepartmentSuccess({ elements })),
            catchError((error) => of(loadReferralsByReferralDepartmentFail({ error }))),
          ),
      ),
    ),
  );

  loadReferralsByJobFunction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadReferralsByJobFunction),
      pluck('filters'),
      switchMap((filters) =>
        this.service.post<ChartElement[]>({ url: 'count-by-job-functions', body: filters }).pipe(
          map((elements) => loadReferralsByJobFunctionSuccess({ elements })),
          catchError((error) => of(loadReferralsByJobFunctionFail({ error }))),
        ),
      ),
    ),
  );

  loadReferralsByReferralFunction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadReferralsByReferralFunction),
      pluck('filters'),
      switchMap((filters) =>
        this.service
          .post<ChartElement[]>({ url: 'count-by-referral-functions', body: filters })
          .pipe(
            map((elements) => loadReferralsByReferralFunctionSuccess({ elements })),
            catchError((error) => of(loadReferralsByReferralFunctionFail({ error }))),
          ),
      ),
    ),
  );

  loadReferralCountByStage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadReferralCountByStage),
      pluck('filters'),
      switchMap((filters) =>
        this.service.post<ReferralCountByStage[]>({ url: 'count-by-stage', body: filters }).pipe(
          map((counts) => loadReferralCountByStageSuccess({ counts })),
          catchError((error) => of(loadReferralCountByStageFail({ error }))),
        ),
      ),
    ),
  );

  getAvgResponseTime = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAvgResponseTime),
      pluck('jobIds'),
      filter(Boolean),
      switchMap((jobIds: number[]) =>
        this.hiringManagerService.getAvgResponseTimeForJobId(jobIds).pipe(
          map((response: AvgResponseTime[]) => loadAvgResponseTimeSuccess({ response })),
          catchError((error) => of(loadAvgResponseTimeFail(error))),
        ),
      ),
    ),
  );

  loadDashboardReportsEffect = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDashboardReports),
      switchMap((params) =>
        this.reportDashboardService.getDashboardReportService(params.filters).pipe(
          map((response: DashboardReportModel) => loadDashboardReportsSuccess({ response })),
          catchError((error) => of(loadDashboardReportsFail({ error }))),
        ),
      ),
    ),
  );

  loadDashboardReportsEffectSubmissions = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDashboardReportsSubmissions),
      switchMap((params) =>
        this.hiringManagerService.getDashboardReportServiceSubmission(params.filters).pipe(
          map((response: DashboardReportModel) =>
            loadDashboardReportsSuccessSubmissions({ response }),
          ),
          catchError((error) => of(loadDashboardReportsFailSubmissions({ error }))),
        ),
      ),
    ),
  );

  loadDashboardReportsEffectOffer = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDashboardReportsOffer),
      switchMap((params) =>
        this.reportDashboardService.getDashboardReportServiceOffer(params.filters).pipe(
          map((response: DashboardReportModel) => loadDashboardReportsSuccessOffer({ response })),
          catchError((error) => of(loadDashboardReportsFailOffer({ error }))),
        ),
      ),
    ),
  );
  loadOldestReferralDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadOldestReferralDate),
      switchMap(() =>
        this.service.get({ url: 'oldest-referral-date' }).pipe(
          map((referralOldestDate) =>
            loadOldestReferralDateSuccess({ referralOldestDate: new Date(referralOldestDate) }),
          ),
          catchError((error) => of(loadOldestReferralDateFail({ error }))),
        ),
      ),
    ),
  );

  loadAveragesPerJob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAveragesPerJob),
      switchMap(({ params }) =>
        this.reportDashboardService.get({ url: 'averages-per-job', params }).pipe(
          map((averagesPerJob) => loadAveragesPerJobSuccess({ averagesPerJob })),
          catchError((error) => of(loadAveragesPerJobFail({ error }))),
        ),
      ),
    ),
  );

  sendEmailReferralReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendEmailReferralReport),
      switchMap(({ params }) =>
        this.metricsReportService.sendEmailReferralReport(params).pipe(
          map((response: DashboardReportModel) => sendEmailSuccessReport()),
          catchError((error) => of(sendEmailSuccessReport())),
        ),
      ),
    ),
  );

  constructor(
    private jobService: JobService,
    private actions$: Actions,
    private store: Store<{}>,
    private service: ReferralReportsService,
    private hiringManagerService: HiringManagerService,
    private metricsReportService: MetricsReportService,
    private reportDashboardService: ReportDashboardService,
    private reportPipelineService: ReportPipelineService,
  ) {}
}
